// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

import * as React from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { withTranslation, WithTranslation } from "react-i18next";
import * as AdaptiveCards from "adaptivecards";
import { Loader, Button, Text, List, Image, Flex } from '@fluentui/react-northstar';
import * as microsoftTeams from "@microsoft/teams-js";

import './sendConfirmationTaskModule.scss';
import { getDraftNotification, getConsentSummaries, sendDraftNotification, getAppSettings } from '../../apis/messageListApi';
import {
    getInitAdaptiveCard, setCardTitle, setCardImageLink, setCardSummary,
    setCardAuthor, setCardBtns, setCardTargetImage, setCardTargetTitle, setCardTarget
} from '../AdaptiveCard/adaptiveCard';
import { ImageUtil } from '../../utility/imageutility';
import { TFunction } from "i18next";

export interface IListItem {
    header: string,
    media: JSX.Element,
}

export interface IMessage {
    id: string,
    title: string,
    acknowledgements?: number,
    reactions?: number,
    responses?: number,
    succeeded?: number,
    failed?: number,
    throttled?: number,
    sentDate?: string,
    imageLink?: string,
    summary?: string,
    author?: string,
    buttonLink?: string,
    buttonTitle?: string,
    buttons: string,
    isImportant?: boolean,
    csvUsers: string,
    channelId?: string,
    channelTitle?: string,
    channelImage?: string,
}

export interface SendConfirmationTaskModuleProps extends RouteComponentProps, WithTranslation {
}

export interface IStatusState {
    message: IMessage;
    loader: boolean;
    teamNames: string[];
    rosterNames: string[];
    groupNames: string[];
    allUsers: boolean;
    messageId: number;
    errorMessage: string | undefined;
}

class SendConfirmationTaskModule extends React.Component<SendConfirmationTaskModuleProps, IStatusState> {
    readonly localize: TFunction;
    targetingEnabled: boolean; // property to store value indicating if the targeting mode is enabled or not
    masterAdminUpns: string; // property to store value with the master admins

    private initMessage = {
        id: "",
        title: "",
        buttons: "[]",
        csvUsers: "",
        channelId: "",
    };

    private card: any;

    constructor(props: SendConfirmationTaskModuleProps) {
        super(props);
        this.localize = this.props.t;
        this.card = getInitAdaptiveCard(this.localize);

        this.state = {
            message: this.initMessage,
            loader: true,
            teamNames: [],
            rosterNames: [],
            groupNames: [],
            allUsers: false,
            messageId: 0,
            errorMessage: undefined
        };
    }

    public componentDidMount() {
        microsoftTeams.initialize();

        let params = this.props.match.params;

        this.getAppSettings().then(() => {
            setCardTarget(this.card, this.targetingEnabled);
            if ('id' in params) {
                let id = params['id'];
                this.getItem(id).then(() => {
                    getConsentSummaries(id).then((response) => {
                        this.setState({
                            teamNames: response.data.teamNames.sort(),
                            rosterNames: response.data.rosterNames.sort(),
                            groupNames: response.data.groupNames.sort(),
                            allUsers: response.data.allUsers,
                            messageId: id,
                        }, () => {
                            this.setState({
                                loader: false
                            }, () => {

                                setCardTargetImage(this.card, this.state.message.channelImage);
                                setCardTargetTitle(this.card, this.state.message.channelTitle);
                                setCardTitle(this.card, this.state.message.title);
                                setCardImageLink(this.card, this.state.message.imageLink);
                                setCardSummary(this.card, this.state.message.summary);
                                setCardAuthor(this.card, this.state.message.author);

                                if (this.state.message.buttonTitle && this.state.message.buttonLink) {
                                    setCardBtns(this.card, [{
                                        "type": "Action.OpenUrl",
                                        "title": this.state.message.buttonTitle,
                                        "url": this.state.message.buttonLink
                                    }]);


                                }
                                else if (this.state.message.buttons) {
                                    setCardBtns(this.card, JSON.parse(this.state.message.buttons));
                                }

                                let adaptiveCard = new AdaptiveCards.AdaptiveCard();
                                adaptiveCard.parse(this.card);
                                let renderedCard = adaptiveCard.render();
                                document.getElementsByClassName('adaptiveCardContainer')[0].appendChild(renderedCard);
                                if (this.state.message.buttonLink) {
                                    let link = this.state.message.buttonLink;
                                    adaptiveCard.onExecuteAction = function (action) { window.open(link, '_blank'); };
                                }
                            });
                        });
                    });
                });
            }
        });
    }

    private getItem = async (id: number) => {
        try {
            const response = await getDraftNotification(id);
            this.setState({
                message: response.data
            });
        } catch (error) {
            return error;
        }
    }

    public render(): JSX.Element {
        if (this.state.errorMessage) {
            return (
                <Flex column className="formContainer" vAlign="center" hAlign="center">
                    {this.state.errorMessage}
                </Flex>
            );
        }
        else if (this.state.loader) {
            return (
                <div className="Loader">
                    <Loader />
                </div>
            );
        } else {
            return (
                <div className="taskModule">
                    <Flex column className="formContainer" vAlign="stretch" gap="gap.small">
                        <Flex className="scrollableContent" gap="gap.small">
                            <Flex.Item size="size.half">
                                <Flex column className="formContentContainer">
                                    <h3>{this.localize("ConfirmToSend")}</h3>
                                    <span>{this.localize("SendToRecipientsLabel")}</span>

                                    <div className="results">
                                        {this.renderAudienceSelection()}
                                    </div>
                                    <h3>{this.localize("Important")}</h3>
                                    <label>{this.renderImportant()}</label>
                                </Flex>
                            </Flex.Item>
                            <Flex.Item size="size.half">
                                <div className="adaptiveCardContainer">
                                </div>
                            </Flex.Item>
                        </Flex>
                        <Flex className="footerContainer" vAlign="end" hAlign="end">
                            <Flex className="buttonContainer" gap="gap.small">
                                <Flex.Item push>
                                    <Loader id="sendingLoader" className="hiddenLoader sendingLoader" size="smallest" label={this.localize("PreparingMessageLabel")} labelPosition="end" />
                                </Flex.Item>
                                <Button content={this.localize("Send")} id="sendBtn" onClick={this.onSendMessage} primary />
                            </Flex>
                        </Flex>
                    </Flex>
                </div>
            );
        }
    }

    private onSendMessage = () => {
        let spanner = document.getElementsByClassName("sendingLoader");
        spanner[0].classList.remove("hiddenLoader");
        sendDraftNotification({ id: this.state.message.id }).then((result) => {
            if (result && result.data && result.data.errorCode) {
                const errorCode = result.data.errorCode;
                this.setState(prevState => {
                    return { ...prevState, errorMessage: this.localize(errorCode) }
                });
            } else {
                microsoftTeams.tasks.submitTask();
            }
        });
    }

    private getItemList = (items: string[]) => {
        let resultedTeams: IListItem[] = [];
        if (items) {
            resultedTeams = items.map((element) => {
                const resultedTeam: IListItem = {
                    header: element,
                    media: <Image src={ImageUtil.makeInitialImage(element)} avatar />
                }
                return resultedTeam;
            });
        }
        return resultedTeams;
    }

    private renderImportant = () => {
        if (this.state.message.isImportant) {
            return (
                <label>Yes</label>
            )
        } else {
            return (
                <label>No</label>
            )
        }
    }

    private renderCSV = () => {
        if (this.state.message.csvUsers.length > 0) {
            return (
                <label>Yes</label>
            )
        } else {
            return (
                <label>No</label>
            )
        }
    }

    // get the app configuration values and set targeting mode from app settings
    private getAppSettings = async () => {
        let response = await getAppSettings();
        if (response.data) {
            this.targetingEnabled = (response.data.targetingEnabled === 'true'); //get the targetingenabled value
            this.masterAdminUpns = response.data.masterAdminUpns; //get the array of master admins
        }
    }

    private renderAudienceSelection = () => {
        if (this.state.teamNames && this.state.teamNames.length > 0) {
            return (
                <div key="teamNames"> <span className="label">{this.localize("TeamsLabel")}</span>
                    <List items={this.getItemList(this.state.teamNames)} />
                </div>
            );
        } else if (this.state.rosterNames && this.state.rosterNames.length > 0) {
            return (
                <div key="rosterNames"> <span className="label">{this.localize("TeamsMembersLabel")}</span>
                    <List items={this.getItemList(this.state.rosterNames)} />
                </div>);
        } else if (this.state.groupNames && this.state.groupNames.length > 0) {
            return (
                <div key="groupNames" > <span className="label">{this.localize("GroupsMembersLabel")}</span>
                    <List items={this.getItemList(this.state.groupNames)} />
                </div>);
        } else if (this.state.message.csvUsers ) {
            return (
                <div key="allUsers">
                    <span className="label">{this.localize("CSVUsersLabel")}</span>
                    <div className="noteText">
                        <Text error content={this.localize("SendToCSVUsersNote")} />
                    </div>
                </div>);
        } else if (this.state.allUsers) {
            return (
                <div key="allUsers">
                    <span className="label">{this.localize("AllUsersLabel")}</span>
                    <div className="noteText">
                        <Text error content={this.localize("SendToAllUsersNote")} />
                    </div>
                </div>);
        } else {
            return (<div></div>);
        }
    }
}

const sendConfirmationTaskModuleWithTranslation = withTranslation()(SendConfirmationTaskModule);
export default sendConfirmationTaskModuleWithTranslation;